import { Component, computed, effect, inject } from '@angular/core'

import { TabBarComponent, TabComponent } from '@hyperkit'

import { HydrationService } from '../../../services/hydration.service'
import { NavigationService } from '../../../services/navigation.service'
import { PackageViewService } from '../../../services/reader/package-view.service'

import {
  MitreTabComponent,
  OverviewTabComponent,
  QueryLogicTabComponent,
  ReferencesTabComponent,
  ResearchTabComponent,
  ResponseActionsTabComponent,
} from './components/index'

type SubTab =
  | 'overview'
  | 'query-logic'
  | 'research'
  | 'references'
  | 'mitre'
  | 'response-actions'

const ALL_TABS: { id: SubTab; text: string }[] = [
  { id: 'overview', text: 'Overview' },
  { id: 'query-logic', text: 'Query Logic' },
  { id: 'research', text: 'Research' },
  { id: 'references', text: 'References' },
  { id: 'mitre', text: 'MITRE' },
  { id: 'response-actions', text: 'Response Actions' },
]

@Component({
  standalone: true,
  selector: 'package-details-default-view',
  imports: [
    TabBarComponent,
    TabComponent,
    MitreTabComponent,
    OverviewTabComponent,
    QueryLogicTabComponent,
    ReferencesTabComponent,
    ResearchTabComponent,
    ResponseActionsTabComponent,
  ],
  templateUrl: './default-view.component.html',
  styleUrls: ['./default-view.component.scss'],
})
export class DefaultViewComponent {
  private readonly nav = inject(NavigationService)
  private readonly hydration = inject(HydrationService)
  private readonly pkgView = inject(PackageViewService)

  readonly subTab = this.hydration.query('subtab', 'overview')

  readonly tabs = computed(() => {
    const v = this.pkgView
    const visible: Record<SubTab, boolean> = {
      overview: true,
      'query-logic': !v.isCustom() && v.hasQueryLogic(),
      research: v.hasResearchData(),
      references: v.hasReferencesData(),
      mitre: v.hasMitreData(),
      'response-actions': v.hasResponseActionsData(),
    }
    return ALL_TABS.filter((t) => visible[t.id])
  })

  constructor() {
    // If the active subtab isn't visible (e.g. a deep-link to a tab with no
    // data), fall back to Overview, which is always present.
    effect(() => {
      const ids = this.tabs().map((t) => t.id)
      if (!ids.includes(this.subTab() as SubTab)) {
        this.nav.setQueryParam('subtab', 'overview')
      }
    })
  }

  selectTab(tab: SubTab | string): void {
    this.nav.setQueryParam('subtab', tab)
  }
}
